/**
 * BugReportWidget — pływający widget „Zgłoś błąd" (tenant scope, front-only).
 *
 * NIE jest modalem: świadomie bez backdropu i bez blokowania tła, bo minimalizacja
 * ma sens tylko wtedy, gdy po zwinięciu można DALEJ klikać w apkę (odtworzyć błąd,
 * zrobić zrzut). Renderuje się przez portal do `document.body`, zakotwiczony w prawym
 * dolnym rogu (jak widgety typu intercom). `z-40` = nad treścią, pod modalami (`z-50`)
 * i toastami (`z-[60]`).
 *
 * Trzy stany z kontekstu: `closed` → null, `minimized` → kafelek, `open` → panel z
 * formularzem (opis + do 5 zrzutów ekranu). „Wyślij" na tym etapie tylko pokazuje toast
 * i czyści draft — backend (zapis zgłoszenia, metadane: kto/tenant/wersja/data, tabela
 * w panelu ITE) dojdzie osobno.
 */

import {
  type DragEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bug, ClipboardPaste, Minus, Send, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Spinner } from '@/components/Spinner';
import { Textarea } from '@/components/Textarea';
import { MAX_BUG_SCREENSHOTS, useBugReport } from '@/contexts/BugReportContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type WidgetTab = 'form' | 'list';
type MyReportStatus = 'new' | 'in_progress' | 'resolved' | 'rejected';

/** Kolor kropki statusu „moich zgłoszeń". */
const MY_STATUS_DOT: Record<MyReportStatus, string> = {
  new: 'bg-blue-500',
  in_progress: 'bg-amber-500',
  resolved: 'bg-green-500',
  rejected: 'bg-text-muted',
};

interface MyReportDto {
  id: string;
  message: string;
  status: MyReportStatus;
  priority: string;
  createdAt: string;
}

function formatShort(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function BugReportWidget() {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    status,
    message,
    screenshots,
    minimize,
    restore,
    close,
    setMessage,
    addScreenshots,
    removeScreenshot,
  } = useBugReport();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pasteArmed, setPasteArmed] = useState(false);
  const [tab, setTab] = useState<WidgetTab>('form');
  // Pozycja panelu po przeciągnięciu (null = domyślna kotwica w prawym dolnym rogu).
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  const dragState = useRef<{ offsetX: number; offsetY: number } | null>(null);
  // Rozmiar panelu po ręcznym powiększeniu (null = domyślny 380px / auto-wysokość).
  const [panelSize, setPanelSize] = useState<{ width: number; height: number } | null>(null);
  // Podczas resize trzymamy stały prawy-dolny róg (kotwica), zmieniamy lewy-górny.
  const resizeState = useRef<{ right: number; bottom: number } | null>(null);

  const queryClient = useQueryClient();
  const isOpen = status === 'open';
  const atLimit = screenshots.length >= MAX_BUG_SCREENSHOTS;

  // Lista „Twoje zgłoszenia" — pobierana gdy zakładka aktywna i panel otwarty.
  const myReportsQuery = useQuery({
    queryKey: ['bug-reports', 'mine'],
    queryFn: async () => {
      const res = await api.get<{ reports: MyReportDto[] }>('/bug-reports');
      return res.data.reports;
    },
    enabled: isOpen && tab === 'list',
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('message', message);
      fd.append('pageUrl', window.location.href);
      for (const shot of screenshots) fd.append('screenshots', shot.file, shot.file.name);
      await api.post('/bug-reports', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.bugReport.submitSuccess') });
      void queryClient.invalidateQueries({ queryKey: ['bug-reports', 'mine'] });
      close();
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.bugReport.submitError') });
    },
  });

  const canSubmit = message.trim().length > 0 && !submitMutation.isPending;

  // Fokus na opisie po otwarciu panelu.
  useEffect(() => {
    if (isOpen) {
      const id = window.setTimeout(() => textareaRef.current?.focus(), 40);
      return () => window.clearTimeout(id);
    }
  }, [isOpen]);

  // ESC zwija panel (draft zostaje) — nie zamyka, żeby nie kasować treści przypadkiem.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') minimize();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, minimize]);

  // Po zamknięciu wróć do domyślnej kotwicy i rozmiaru na następne otwarcie.
  useEffect(() => {
    if (status === 'closed') {
      setPanelPos(null);
      setPanelSize(null);
    }
  }, [status]);

  const ingest = useCallback(
    (files: FileList | File[]) => {
      const { rejected, overLimit } = addScreenshots(files);
      if (rejected > 0) {
        toast.show({ variant: 'warning', title: t('tenant.bugReport.screenshots.rejectedImages') });
      }
      if (overLimit > 0) {
        toast.show({
          variant: 'warning',
          title: t('tenant.bugReport.screenshots.overLimit', { max: MAX_BUG_SCREENSHOTS }),
        });
      }
    },
    [addScreenshots, toast, t],
  );

  // Wklejanie zrzutu ze schowka (Ctrl/Cmd+V) — gdy okno jest w użyciu (otwarte lub
  // zminimalizowane); paste ze zminimalizowanego przywraca panel. NIE łapiemy paste, gdy
  // widget jest zamknięty (żeby nie przechwytywać wklejania w innych miejscach aplikacji).
  // Reagujemy tylko na OBRAZ — zwykły paste tekstu i edytory rich-text zostają nietknięte.
  useEffect(() => {
    if (status === 'closed') return;
    const handler = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const images: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) images.push(file);
        }
      }
      if (images.length === 0) return; // brak obrazu → nie ruszamy zwykłego paste
      e.preventDefault();
      if (status === 'minimized') restore(); // wyciągnij panel na wierzch
      ingest(images);
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
  }, [status, restore, ingest]);

  // Przeciąganie panelu za nagłówek.
  function startDrag(e: ReactMouseEvent<HTMLDivElement>) {
    // Nie zaczynaj przeciągania od kliknięcia w przycisk (minimalizuj/zamknij).
    if ((e.target as HTMLElement).closest('button')) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = { offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
    setPanelPos({ top: rect.top, left: rect.left });
    e.preventDefault();
  }

  useEffect(() => {
    function onMove(e: MouseEvent) {
      const drag = dragState.current;
      const rect = panelRef.current?.getBoundingClientRect();
      if (!drag || !rect) return;
      const maxLeft = window.innerWidth - rect.width - 8;
      const maxTop = window.innerHeight - rect.height - 8;
      setPanelPos({
        left: Math.min(Math.max(8, e.clientX - drag.offsetX), Math.max(8, maxLeft)),
        top: Math.min(Math.max(8, e.clientY - drag.offsetY), Math.max(8, maxTop)),
      });
    }
    function onUp() {
      dragState.current = null;
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Powiększanie panelu — uchwyt w lewym-górnym rogu; prawy-dolny róg (kotwica) zostaje.
  function startResize(e: ReactMouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    e.preventDefault();
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    resizeState.current = { right: rect.right, bottom: rect.bottom };
    setPanelPos({ top: rect.top, left: rect.left });
    setPanelSize({ width: rect.width, height: rect.height });
  }

  useEffect(() => {
    function onMove(e: MouseEvent) {
      const rs = resizeState.current;
      if (!rs) return;
      const minW = 320;
      const minH = 300;
      const maxW = Math.min(760, window.innerWidth - 16);
      const maxH = window.innerHeight - 16;
      let left = Math.max(8, e.clientX);
      let top = Math.max(8, e.clientY);
      let width = rs.right - left;
      let height = rs.bottom - top;
      if (width < minW) {
        width = minW;
        left = rs.right - minW;
      }
      if (width > maxW) {
        width = maxW;
        left = rs.right - maxW;
      }
      if (height < minH) {
        height = minH;
        top = rs.bottom - minH;
      }
      if (height > maxH) {
        height = maxH;
        top = rs.bottom - maxH;
      }
      setPanelPos({ top, left });
      setPanelSize({ width, height });
    }
    function onUp() {
      resizeState.current = null;
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (atLimit) return;
    ingest(e.dataTransfer.files);
  }

  function handleSubmit() {
    if (message.trim().length === 0 || submitMutation.isPending) return;
    submitMutation.mutate();
  }

  if (status === 'closed') return null;

  // ── Kafelek zminimalizowany ─────────────────────────────────────────────────
  if (status === 'minimized') {
    return createPortal(
      <div
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full border border-border-secondary bg-bg-elevated py-1.5 pl-3 pr-1.5 shadow-lg"
        data-testid="tenant-bug-report-minimized"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={restore}
          leftIcon={<Bug size={16} className="text-accent-600" aria-hidden="true" />}
          className="!px-1 text-sm font-medium text-text-primary"
          data-testid="tenant-bug-report-restore"
        >
          {t('tenant.bugReport.minimizedLabel')}
        </Button>
        <IconButton
          size="sm"
          aria-label={t('common.close')}
          onClick={close}
          data-testid="tenant-bug-report-minimized-close"
        >
          <X aria-hidden="true" />
        </IconButton>
      </div>,
      document.body,
    );
  }

  // ── Pełny panel ─────────────────────────────────────────────────────────────
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('tenant.bugReport.title')}
      style={{
        ...(panelPos ? { top: panelPos.top, left: panelPos.left } : {}),
        ...(panelSize ? { width: panelSize.width, height: panelSize.height } : {}),
      }}
      className={cn(
        'fixed z-40 flex max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-border-secondary bg-bg-elevated shadow-2xl',
        !panelPos && 'bottom-6 right-6',
        !panelSize && 'max-h-[calc(100vh-3rem)] w-[380px]',
      )}
      data-testid="tenant-bug-report-panel"
    >
      {/* Uchwyt powiększania — lewy górny róg (prawy-dolny to kotwica). */}
      <div
        onMouseDown={startResize}
        role="separator"
        aria-label={t('tenant.bugReport.resize')}
        title={t('tenant.bugReport.resize')}
        className="absolute left-0 top-0 z-10 h-4 w-4 cursor-nwse-resize"
        data-testid="tenant-bug-report-resize"
      />
      {/* Nagłówek — uchwyt przeciągania (cursor-move); przyciski wyłączone z drag w startDrag). */}
      <div
        onMouseDown={startDrag}
        className="flex cursor-move select-none items-center gap-2 border-b border-border-secondary px-4 py-3"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-500/10 text-accent-600">
          <Bug size={18} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-text-primary">
            {t('tenant.bugReport.title')}
          </p>
        </div>
        <IconButton
          size="sm"
          aria-label={t('tenant.bugReport.minimize')}
          title={t('tenant.bugReport.minimize')}
          onClick={minimize}
          data-testid="tenant-bug-report-minimize"
        >
          <Minus aria-hidden="true" />
        </IconButton>
        <IconButton
          size="sm"
          aria-label={t('common.close')}
          title={t('common.close')}
          onClick={close}
          data-testid="tenant-bug-report-close"
        >
          <X aria-hidden="true" />
        </IconButton>
      </div>

      {/* Zakładki: Zgłoś błąd / Twoje zgłoszenia */}
      <div className="flex gap-1 border-b border-border-secondary px-3 pt-2" role="tablist">
        {[
          { key: 'form' as const, label: t('tenant.bugReport.tabs.new') },
          { key: 'list' as const, label: t('tenant.bugReport.tabs.mine') },
        ].map((it) => (
          <div
            key={it.key}
            role="tab"
            tabIndex={0}
            aria-selected={tab === it.key}
            onClick={() => setTab(it.key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setTab(it.key);
              }
            }}
            className={cn(
              'cursor-pointer border-b-2 px-2 pb-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
              tab === it.key
                ? 'border-accent-500 text-accent-600'
                : 'border-transparent text-text-secondary hover:text-text-primary',
            )}
            data-testid={`tenant-bug-report-tab-${it.key}`}
          >
            {it.label}
          </div>
        ))}
      </div>

      {/* Treść (przewijalna) — formularz zgłoszenia */}
      {tab === 'form' && (
        <div className="flex-1 space-y-3 overflow-auto px-4 py-3">
          <p className="text-xs text-text-tertiary">{t('tenant.bugReport.intro')}</p>

          <Textarea
            ref={textareaRef}
            label={t('tenant.bugReport.messageLabel')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder={t('tenant.bugReport.messagePlaceholder')}
            data-testid="tenant-bug-report-message"
          />

          {/* Zrzuty ekranu */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-sm font-medium text-text-secondary">
                {t('tenant.bugReport.screenshots.label')}
              </span>
              <span
                className={cn('text-xs', atLimit ? 'text-accent-600' : 'text-text-muted')}
                data-testid="tenant-bug-report-counter"
              >
                {screenshots.length}/{MAX_BUG_SCREENSHOTS}
              </span>
            </div>

            {screenshots.length > 0 && (
              <div
                className="mb-2 grid grid-cols-4 gap-2"
                data-testid="tenant-bug-report-thumbnails"
              >
                {screenshots.map((shot) => (
                  <div
                    key={shot.id}
                    className="group relative aspect-square overflow-hidden rounded-lg border border-border-secondary bg-bg-tertiary"
                  >
                    <img
                      src={shot.previewUrl}
                      alt={shot.file.name}
                      title={shot.file.name}
                      className="h-full w-full object-cover"
                    />
                    <IconButton
                      size="sm"
                      onClick={() => removeScreenshot(shot.id)}
                      aria-label={t('tenant.bugReport.screenshots.remove')}
                      title={t('tenant.bugReport.screenshots.remove')}
                      className="absolute right-1 top-1 h-6 w-6 rounded-full bg-black/60 text-white opacity-0 transition-opacity hover:bg-black/80 hover:text-white group-hover:opacity-100 [&_svg]:h-3.5 [&_svg]:w-3.5"
                      data-testid={`tenant-bug-report-remove-${shot.id}`}
                    >
                      <X aria-hidden="true" />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}

            {!atLimit && (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                onFocus={() => setPasteArmed(true)}
                onBlur={() => setPasteArmed(false)}
                onMouseEnter={() => setPasteArmed(true)}
                onMouseLeave={() => setPasteArmed(false)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                data-testid="tenant-bug-report-dropzone"
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-3 py-4 text-center transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
                  dragOver || pasteArmed
                    ? 'border-accent-500 bg-accent-500/10'
                    : 'border-accent-500/40 bg-accent-500/5 hover:border-accent-500/60',
                )}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-500/15 text-accent-600">
                  <ClipboardPaste className="h-4 w-4" aria-hidden="true" />
                </span>
                <p className="text-xs font-semibold text-accent-600">
                  {t('tenant.bugReport.screenshots.pasteTitle')}
                </p>
                <p className="text-[11px] text-text-muted">
                  {t('tenant.bugReport.screenshots.pasteHint', { max: MAX_BUG_SCREENSHOTS })}
                </p>
                {/* Brak prymitywu file-input w bibliotece — ukryty natywny input jest tu kanoniczny
                  (ten sam wzorzec co AttachmentsSection). */}
                {/* eslint-disable-next-line no-restricted-syntax */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    ingest(e.target.files ?? []);
                    e.target.value = '';
                  }}
                  data-testid="tenant-bug-report-file-input"
                />
              </div>
            )}
          </div>

          <p className="text-[11px] leading-relaxed text-text-muted">
            {t('tenant.bugReport.metaNote')}
          </p>
        </div>
      )}

      {/* Treść — lista „Twoje zgłoszenia" */}
      {tab === 'list' && (
        <div className="flex-1 overflow-auto px-4 py-3" data-testid="tenant-bug-report-my-list">
          {myReportsQuery.isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : myReportsQuery.isError ? (
            <p className="py-6 text-center text-sm text-error-500">
              {t('tenant.bugReport.mine.loadError')}
            </p>
          ) : (myReportsQuery.data?.length ?? 0) === 0 ? (
            <p className="py-6 text-center text-sm text-text-muted">
              {t('tenant.bugReport.mine.empty')}
            </p>
          ) : (
            <ul className="space-y-2">
              {myReportsQuery.data?.map((r) => (
                <li
                  key={r.id}
                  className="rounded-lg border border-border-secondary bg-bg-primary px-3 py-2.5"
                >
                  <p className="text-sm text-text-primary">{r.message}</p>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs text-text-secondary">
                      <span
                        aria-hidden="true"
                        className={cn('h-2 w-2 rounded-full', MY_STATUS_DOT[r.status])}
                      />
                      {t(`tenant.bugReport.status.${r.status}`)}
                    </span>
                    <span className="font-mono text-[11px] text-text-muted">
                      {formatShort(r.createdAt)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Stopka */}
      {tab === 'form' ? (
        <div className="flex items-center justify-end gap-2 border-t border-border-secondary px-4 py-3">
          <Button variant="ghost" size="sm" onClick={close} data-testid="tenant-bug-report-cancel">
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
            leftIcon={<Send size={16} aria-hidden="true" />}
            data-testid="tenant-bug-report-submit"
          >
            {t('tenant.bugReport.submit')}
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-end border-t border-border-secondary px-4 py-3">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setTab('form')}
            leftIcon={<Bug size={16} aria-hidden="true" />}
            data-testid="tenant-bug-report-new"
          >
            {t('tenant.bugReport.mine.newReport')}
          </Button>
        </div>
      )}
    </div>,
    document.body,
  );
}
